// This hook manages product image handling including formatting, retrieving, and validating image URLs

type ImageFormat = 'default' | 'thumbnail' | 'alternative';

export const useProductImage = () => {
  const formatProductCode = (productName: string): string => {
    if (!productName) return '';
    
    return productName
      .toLowerCase()
      .replace(/fosser\s+/i, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .trim();
  };

  // Updated fallback image to use the new Fosser Premium VS 5W-40 image
  const fallbackImage = '/lovable-uploads/15091522-dec3-4b8f-b624-9acefcbb3eee.png';

  const getImageUrl = (productName: string, format: ImageFormat = 'default'): string => {
    // Default to fallback image if name is empty
    if (!productName) {
      return fallbackImage;
    }
    
    const productCode = formatProductCode(productName);
    
    // Return appropriate URL format based on the requested format
    switch (format) {
      case 'default':
        return `https://fosser.de/app/uploads/2022/06/${productCode}.png`;
      case 'thumbnail':
        return `https://fosser.de/app/uploads/2020/06/${productCode}-400x400.png`;
      case 'alternative':
        return `https://fosser.de/app/themes/fosser/dist/images/products/${productCode}.png`;
      default:
        return `https://fosser.de/app/uploads/2022/06/${productCode}.png`;
    }
  };

  // Try all possible image formats one by one
  const getAllPossibleImageUrls = (productName: string): string[] => {
    if (!productName) return [fallbackImage];
    
    const formats: ImageFormat[] = ['default', 'thumbnail', 'alternative'];
    return formats.map(format => getImageUrl(productName, format));
  };

  // Check if an image URL is valid using a HEAD request
  const checkImageUrl = async (url: string): Promise<boolean> => {
    try {
      // Skip validation for data URLs, blob URLs, and local uploads to avoid CORS issues
      if (url.startsWith('data:') || url.startsWith('/') || url.startsWith('blob:')) {
        return true;
      }
      
      // Skip validation for Fosser images - they often fail the HEAD request but work
      if (url.includes('fosser.de')) {
        return true;
      }
      
      // For remote URLs, check with a HEAD request
      const response = await fetch(url, { 
        method: 'HEAD',
        // Add cache control to avoid caching issues
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      return response.ok;
    } catch (error) {
      console.error('Error checking image URL:', error);
      return false;
    }
  };

  // Find first working image URL from all possible formats
  const findWorkingImageUrl = async (productName: string): Promise<string> => {
    if (!productName) return fallbackImage;
    
    const urls = getAllPossibleImageUrls(productName);
    
    for (const url of urls) {
      try {
        const isValid = await checkImageUrl(url);
        if (isValid) {
          console.log('Found working image URL:', url);
          return url;
        }
      } catch (error) {
        console.error('Error checking URL:', url, error);
      }
    }
    
    console.log('No working image URL found, returning fallback');
    return fallbackImage;
  };

  // Safely retrieve an image URL, with fallback handling
  const getSafeImageUrl = (imageUrl: string | null | undefined): string => {
    if (!imageUrl) return fallbackImage;
    
    // Handle different URL formats and paths correctly
    if (imageUrl.startsWith('http://') || 
        imageUrl.startsWith('https://') || 
        imageUrl.startsWith('data:') || 
        imageUrl.startsWith('blob:')) {
      return imageUrl;
    }
    
    // Handle lovable uploads from the server
    if (imageUrl.startsWith('/lovable-uploads/')) {
      return imageUrl;
    }
    
    // If it starts with a slash, ensure it's treated as a relative path
    if (imageUrl.startsWith('/')) {
      // It's likely a server-relative path, return as is
      return imageUrl;
    }
    
    // Check if it's a Supabase storage URL without protocol
    if (imageUrl.includes('supabase') && !imageUrl.startsWith('http')) {
      return `https://${imageUrl}`;
    }
    
    // If the image URL is a local file path that wasn't caught by the above checks
    if (imageUrl.includes('/') && !imageUrl.startsWith('http')) {
      // Assume it's a relative path
      return `/${imageUrl.replace(/^\/+/, '')}`;
    }
    
    // Otherwise, return the fallback
    console.log('Using fallback image for:', imageUrl);
    return fallbackImage;
  };

  // Check if URL is a direct image URL
  const isImageUrl = (url: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.avif', '.svg'];
    return imageExtensions.some(ext => url.toLowerCase().endsWith(ext));
  };

  // Validate if a URL might be a direct image URL
  const validateImageUrl = (url: string): boolean => {
    if (!url) return false;
    
    try {
      const urlObj = new URL(url);
      return isImageUrl(urlObj.pathname);
    } catch (e) {
      // If it's not a valid URL, check if it might be a local path
      return url.startsWith('/') || isImageUrl(url);
    }
  };

  return {
    formatProductCode,
    getImageUrl,
    getAllPossibleImageUrls,
    checkImageUrl,
    findWorkingImageUrl,
    getSafeImageUrl,
    isImageUrl,
    validateImageUrl,
    fallbackImage
  };
};
